<template>
  <div>
    <q-table
      flat
      bordered
      row-key="id"
      :data="coupons"
      :columns="columns"
      :pagination="pagination"
      :loading="loading"
      :no-data-label="$t('no_coupons_registered')"
    >
      <template v-slot:body-cell-type="props">
        <q-td :props="props">
          {{ $t(props.row.type.toLowerCase()) }}
        </q-td>
      </template>

      <template v-slot:body-cell-site_name="props">
        <q-td :props="props">
          {{ props.row.type === 'EXCLUSIVE' ? props.row.site_name || '-' : '-' }}
        </q-td>
      </template>

      <template v-slot:body-cell-validity_period="props">
        <q-td :props="props">
          {{ formatValidityPeriod(props.row.valid_from, props.row.valid_until) }}
        </q-td>
      </template>

      <template v-slot:body-cell-status="props">
        <q-td :props="props">
          <q-badge :color="couponAvailabilityColor(props.row)">
            {{ couponAvailabilityLabel(props.row) }}
          </q-badge>
        </q-td>
      </template>

      <template v-slot:body-cell-action="props">
        <q-td :props="props">
          <q-btn
            unelevated
            round
            flat
            icon="mdi-pencil-outline"
            color="primary"
            @click="$emit('edit', props.row)"
          >
            <q-tooltip>{{ $t('edit') }}</q-tooltip>
          </q-btn>

          <q-btn
            unelevated
            round
            flat
            :icon="props.row.status === 1 ? 'mdi-cancel' : 'mdi-check-circle-outline'"
            :color="props.row.status === 1 ? 'negative' : 'positive'"
            @click="toggleStatus(props.row)"
          >
            <q-tooltip>{{ props.row.status === 1 ? $t('deactivate') : $t('activate') }}</q-tooltip>
          </q-btn>
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script>
import AdvertiserService from '../../services/AdvertiserService';
import CampaignMixin from '../../mixins/CampaignMixin';

export default {
  name: 'ListCampaignCoupons',
  mixins: [AdvertiserService, CampaignMixin],
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    coupons: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      columns: [
        {
          name: 'code',
          align: 'left',
          label: this.$t('coupon'),
          field: 'code',
          sortable: true
        },
        {
          name: 'type',
          align: 'left',
          label: this.$t('coupon_type'),
          field: 'type',
          sortable: true
        },
        {
          name: 'site_name',
          align: 'left',
          label: this.$t('channel'),
          field: 'site_name'
        },
        {
          name: 'usage_count',
          align: 'left',
          label: this.$t('usages'),
          field: 'usage_count',
          sortable: true
        },
        {
          name: 'validity_period',
          align: 'left',
          label: this.$t('validity_period'),
          field: 'validity_period'
        },
        {
          name: 'status',
          align: 'left',
          label: 'Status',
          field: 'status'
        },
        {
          name: 'action',
          align: 'center',
          label: this.$t('actions')
        }
      ],
      pagination: {
        sortBy: 'created_at',
        descending: true,
        rowsPerPage: 10
      }
    };
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${day}/${month}/${year}`;
    },

    couponAvailability(coupon) {
      if (coupon && coupon.availability) {
        return coupon.availability;
      }

      if (!coupon || Number(coupon.status) !== 1) {
        return 'inactive';
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (coupon.valid_from) {
        const from = new Date(`${coupon.valid_from}T00:00:00`);
        if (today < from) {
          return 'scheduled';
        }
      }
      if (coupon.valid_until) {
        const until = new Date(`${coupon.valid_until}T23:59:59`);
        if (today > until) {
          return 'expired';
        }
      }

      return 'active';
    },

    couponAvailabilityLabel(coupon) {
      const state = this.couponAvailability(coupon);
      if (state === 'expired') {
        return this.$t('coupon_expired');
      }
      if (state === 'scheduled') {
        return this.$t('coupon_scheduled');
      }
      if (state === 'inactive') {
        return this.$t('inactive');
      }

      return this.$t('active');
    },

    couponAvailabilityColor(coupon) {
      const state = this.couponAvailability(coupon);
      if (state === 'expired') {
        return 'warning';
      }
      if (state === 'scheduled') {
        return 'info';
      }
      if (state === 'inactive') {
        return 'negative';
      }

      return 'positive';
    },

    formatValidityPeriod(validFrom, validUntil) {
      const from = this.formatDate(validFrom);
      const until = this.formatDate(validUntil);

      if (from && until) {
        return this.$t('coupon_validity_range', { from, until });
      }
      if (from) {
        return this.$t('coupon_valid_from', { date: from });
      }
      if (until) {
        return this.$t('coupon_valid_until', { date: until });
      }
      return this.$t('coupon_no_validity');
    },

    async toggleStatus(coupon) {
      const newStatus = coupon.status === 1 ? 0 : 1;
      const confirmMessage = newStatus === 1 ? this.$t('confirm_activate_coupon') : this.$t('confirm_deactivate_coupon');

      this.$q
        .dialog({
          title: this.$t('confirmation'),
          message: confirmMessage,
          cancel: true,
          persistent: true
        })
        .onOk(async () => {
          this.onLoading(true);
          try {
            const { data, status } = await this.toggleCampaignCouponStatus(coupon.id, newStatus);
            if (status === 200) {
              this.successNotify(data.message);
              this.$emit('refresh');
            }
          } catch (error) {
            const message =
              error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : this.$t('problem_try_again_later');
            this.errorNotify(message);
          } finally {
            this.onLoading(false);
          }
        });
    }
  }
};
</script>
